import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { X, FileText } from "lucide-react";
import { useState, useCallback, useMemo } from "react";
import { Fragment } from "react";

import { MultiFileUpload } from "@/modules/dashboard/payments/components/MultiFileUpload";
import { Button } from "@/shared/components/form/Button";
import { InputSelect } from "@/shared/components/form/InputSelect";
import { InputText } from "@/shared/components/form/InputText";
import type { NewsTranslations } from "@/shared/lang/interfaces/news";
import useTranslations from "@/shared/lang/useTranslations";

import { NOTE_CATEGORY_OPTIONS, NOTE_PRIORITY_OPTIONS } from "../constants/news.constants";
import type { CreateNoteRequest, NoteCategory, NotePriority } from "../types/notes-api.types";

const TITLE_MIN = 3;
const TITLE_MAX = 255;

type SelectOption = { value: string; label: string };

export type NewNoteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateNoteRequest, attachments: File[]) => void;
  isLoading?: boolean;
};

export const NewNoteModal = ({ isOpen, onClose, onSubmit, isLoading = false }: NewNoteModalProps) => {
  const { t } = useTranslations("news");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState<SelectOption | null>(null);
  const [priority, setPriority] = useState<SelectOption | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [priorityError, setPriorityError] = useState<string | null>(null);

  const categoryOptions = useMemo(
    () =>
      NOTE_CATEGORY_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(`category_${opt.value}` as keyof NewsTranslations) as string,
      })),
    [t],
  );
  const priorityOptions = useMemo(
    () =>
      NOTE_PRIORITY_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(`priority_${opt.value}` as keyof NewsTranslations) as string,
      })),
    [t],
  );

  const resetForm = useCallback(() => {
    setTitle("");
    setContent("");
    setCategory(null);
    setPriority(null);
    setIsPinned(false);
    setAttachments([]);
    setTitleError(null);
    setContentError(null);
    setCategoryError(null);
    setPriorityError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(() => {
    const titleTrim = title.trim();
    const contentTrim = content.trim();
    let valid = true;

    if (!titleTrim) {
      setTitleError(t("validation_title_required"));
      valid = false;
    } else if (titleTrim.length < TITLE_MIN || titleTrim.length > TITLE_MAX) {
      setTitleError(t("validation_title_length", { min: TITLE_MIN, max: TITLE_MAX }));
      valid = false;
    } else {
      setTitleError(null);
    }

    if (!contentTrim) {
      setContentError(t("validation_content_required"));
      valid = false;
    } else {
      setContentError(null);
    }

    if (!category?.value) {
      setCategoryError(t("validation_category_required"));
      valid = false;
    } else {
      setCategoryError(null);
    }

    if (!priority?.value) {
      setPriorityError(t("validation_priority_required"));
      valid = false;
    } else {
      setPriorityError(null);
    }

    if (!valid) return;

    const payload: CreateNoteRequest = {
      title: titleTrim,
      content: contentTrim,
      category: category!.value as NoteCategory,
      priority: priority!.value as NotePriority,
      is_pinned: isPinned === true,
      status: "ACTIVE",
    };

    onSubmit(payload, attachments);
  }, [title, content, category, priority, isPinned, attachments, onSubmit, t]);

  return (
    <Transition appear show={isOpen} as={Fragment} afterLeave={resetForm}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[560px] rounded-lg shadow-2xl overflow-hidden border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 transform transition-all">
                <div className="flex items-center justify-between px-8 pt-8 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-green-500/10">
                      <FileText className="h-6 w-6 text-green-500 dark:text-green-400" />
                    </div>
                    <h2 className="text-gray-900 dark:text-white text-2xl font-bold tracking-tight">
                      {t("modal_new_title")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1"
                    aria-label={t("modal_close_aria")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-8 py-4 space-y-4">
                  <InputText
                    label={t("modal_label_title")}
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (titleError) setTitleError(null);
                    }}
                    placeholder={t("modal_title_placeholder")}
                    disabled={isLoading}
                    errorMessage={titleError ?? undefined}
                    id="new-note-title"
                    maxWidth="max-w-full"
                    required
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputSelect<SelectOption>
                      label={t("modal_label_category")}
                      options={categoryOptions}
                      value={category}
                      onChange={(v) => {
                        setCategory(v as SelectOption | null);
                        if (categoryError) setCategoryError(null);
                      }}
                      placeholder={t("modal_category_placeholder")}
                      required
                      isDisabled={isLoading}
                      errorMessage={categoryError ?? undefined}
                      maxWidth="max-w-full"
                    />
                    <InputSelect<SelectOption>
                      label={t("modal_label_priority")}
                      options={priorityOptions}
                      value={priority}
                      onChange={(v) => {
                        setPriority(v as SelectOption | null);
                        if (priorityError) setPriorityError(null);
                      }}
                      placeholder={t("modal_priority_placeholder")}
                      required
                      isDisabled={isLoading}
                      errorMessage={priorityError ?? undefined}
                      maxWidth="max-w-full"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="new-note-content"
                      className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1"
                    >
                      {t("modal_label_content")} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="new-note-content"
                      value={content}
                      onChange={(e) => {
                        setContent(e.target.value);
                        if (contentError) setContentError(null);
                      }}
                      placeholder={t("modal_content_placeholder")}
                      disabled={isLoading}
                      rows={4}
                      className="w-full resize-none px-4 py-2.5 bg-slate-50 dark:bg-zinc-800 border-0 rounded-xl focus:ring-2 focus:ring-green-500 text-sm text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 disabled:opacity-50"
                      aria-invalid={!!contentError}
                      aria-describedby={contentError ? "new-note-content-error" : undefined}
                    />
                    {contentError && (
                      <p id="new-note-content-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {contentError}
                      </p>
                    )}
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPinned}
                      onChange={(e) => setIsPinned(e.target.checked)}
                      disabled={isLoading}
                      className="rounded border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-green-600 focus:ring-green-500 focus:ring-offset-0 accent-green-600 dark:accent-green-500"
                      aria-label={t("modal_pin_aria")}
                    />
                    <span className="text-sm text-gray-700 dark:text-zinc-300">{t("modal_pin_label")}</span>
                  </label>

                  <MultiFileUpload
                    value={attachments}
                    onChange={setAttachments}
                    label={t("modal_label_attachments")}
                    description={t("modal_attachments_description")}
                    instructionText={t("modal_attachments_instruction")}
                    allowPdf
                    maxSizeMB={10}
                    disabled={isLoading}
                    inputId="new-note-attachments"
                  />
                </div>

                <div className="px-8 py-8 flex items-center justify-end gap-3">
                  <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                    {t("modal_cancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    isLoading={isLoading}
                  >
                    {isLoading ? t("modal_creating") : t("modal_create_button")}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
